import React from 'react';
import ContextQuestionCard from './ContextQuestionCard';
import { BomAnswersDisclosure, ExactPartsDisclosure } from './BomAnswersDisclosure';
import { componentSections } from './contextQuestions';
import type { BomAnswer, ContextQuestion, ExactPart } from './useContextStep';

/**
 * The step's body: BOM-wide questions first, then each component as its own
 * bordered block on a canvas gutter so one component never runs into the next.
 * A block holds the component's open questions, numbered straight through so
 * the count in the card header matches the last number, and at its foot the
 * collapsed line of what Robbie already read off the BOM for it. Rows sourced
 * by exact part number close the list.
 */
export interface ContextQuestionListProps {
  questions: ContextQuestion[];
  bomAnswers?: BomAnswer[];
  exactParts?: ExactPart[];
  answers: Record<string, unknown>;
  onAnswer: (field: string, value: unknown) => void;
  readOnly: boolean;
}

const ComponentHeader: React.FC<{ name: string; count: number }> = ({ name, count }) => (
  <div className="flex items-center justify-between gap-s4 border-b border-line bg-surface px-s6 py-[12px]">
    <span className="min-w-0 truncate text-card-header text-ink">{name}</span>
    <span className="shrink-0 text-meta text-ink-muted">
      {count === 0 ? 'Nothing to ask' : `${count} ${count === 1 ? 'question' : 'questions'}`}
    </span>
  </div>
);

const NONE: never[] = [];

export const ContextQuestionList: React.FC<ContextQuestionListProps> = ({
  questions,
  bomAnswers = NONE,
  exactParts = NONE,
  answers,
  onAnswer,
  readOnly,
}) => {
  const { bomWide, components } = componentSections(questions, bomAnswers);
  let number = 0;
  const card = (q: ContextQuestion, isLast: boolean) => {
    number += 1;
    return (
      <ContextQuestionCard
        key={q.field}
        index={number}
        question={q}
        value={answers[q.field]}
        onChange={(v) => onAnswer(q.field, v)}
        isLast={isLast}
        readOnly={readOnly}
      />
    );
  };
  const hasBlocks = components.length > 0 || exactParts.length > 0;

  return (
    <>
      {bomWide.map((q, i) => card(q, !hasBlocks && i === bomWide.length - 1))}
      {hasBlocks && (
        <div className={`flex flex-col gap-s5 bg-canvas p-s5 ${bomWide.length > 0 ? 'border-t border-line' : ''}`}>
          {components.map((section) => (
            <section
              key={section.componentKey}
              aria-label={section.componentName}
              className="overflow-hidden rounded-card border border-line bg-surface"
            >
              <ComponentHeader name={section.componentName} count={section.questions.length} />
              {section.questions.map((q, i) =>
                card(q, section.bomAnswers.length === 0 && i === section.questions.length - 1),
              )}
              {section.bomAnswers.length > 0 && (
                <BomAnswersDisclosure
                  answers={section.bomAnswers}
                  values={answers}
                  onChange={onAnswer}
                  readOnly={readOnly}
                />
              )}
            </section>
          ))}
          {exactParts.length > 0 && (
            <section aria-label="Exact parts" className="overflow-hidden rounded-card border border-line bg-surface">
              <ExactPartsDisclosure parts={exactParts} />
            </section>
          )}
        </div>
      )}
    </>
  );
};

export default ContextQuestionList;
